import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Storefront, ArrowRight, PencilSimple, ArrowSquareOut, Sparkle, Globe,
  Plus, LinkSimple,
} from "@phosphor-icons/react";

/**
 * FarmersWebsitePreview — live thumbnail(s) of the signed-in farmer's
 * website(s), shown on the /farmers home page.
 *
 * Behavior:
 *   • Signed out → cross-promo CTA (Start from scratch / Clone from URL).
 *   • Signed in with 0 sites → 2 clear paths to create.
 *   • Signed in with 1+ sites → visual preview card per site with Edit +
 *     View-live buttons + published/draft badge.
 */
export default function FarmersWebsitePreview() {
  const { user } = useAuth();
  const [sites, setSites] = useState(null); // null = loading
  useEffect(() => {
    if (!user) { setSites([]); return; }
    http.get("/farmers/websites/mine")
      .then((r) => setSites(r.data.websites || []))
      .catch(() => setSites([]));
  }, [user]);

  // Signed-out / not-yet-loaded → simple promo card
  if (!user || sites === null) {
    return (
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8">
        <PromoCard signedIn={!!user} loading={sites === null} />
      </section>
    );
  }

  // Signed in with no sites yet → onboarding CTA
  if (sites.length === 0) {
    return (
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8">
        <PromoCard signedIn />
      </section>
    );
  }

  // Has 1+ sites → live previews
  return (
    <section
      className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8"
      data-testid="farmers-website-preview"
    >
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Storefront size={12} weight="fill" className="inline mr-1" />
            Your Farm Website{sites.length > 1 ? "s" : ""}
          </div>
          <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">
            {sites.length === 1
              ? "Jump back into your farm site."
              : `You have ${sites.length} farm sites. Pick one to edit.`}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/farmers/websites/import"
            data-testid="farmers-preview-clone"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >
            <LinkSimple size={11} weight="bold" /> Clone from URL
          </Link>
          <Link
            to="/farmers/websites/new"
            data-testid="farmers-preview-new"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> New site
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sites.slice(0, 6).map((s) => <SiteCard key={s.id} site={s} />)}
      </div>
    </section>
  );
}

function SiteCard({ site }) {
  const accent = site?.theme_overrides?.brand_color || site?.brand_color || "#B85F2A";
  const isPublished = site.status === "published";
  const hero = site.hero_image_url;

  return (
    <article
      data-testid={`farmers-site-card-${site.slug}`}
      className="group relative overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm hover:shadow-md transition"
    >
      {/* Thumbnail — hero image if present, else colored gradient with logo */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-neutral-100"
        style={hero ? {} : { background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 60%, #241209 100%)` }}
      >
        {hero ? (
          <img
            src={hero}
            alt={site.farm_name}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {site.logo_url ? (
              <img src={site.logo_url} alt="" className="max-h-24 max-w-[60%] object-contain drop-shadow-lg" />
            ) : (
              <div className="text-center px-6">
                <Sparkle size={22} weight="fill" className="mx-auto text-white/80" />
                <div className="mt-1 font-serif-display text-2xl text-white">{site.farm_name}</div>
                {site.tagline && (
                  <div className="mt-1 text-xs text-white/85 line-clamp-2">{site.tagline}</div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Status badge */}
        <span
          className={`absolute top-2 right-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase backdrop-blur ${
            isPublished
              ? "bg-emerald-500/90 text-white"
              : "bg-white/85 text-[#3A3830]"
          }`}
        >
          {isPublished ? "Published" : "Draft"}
        </span>
        {site.logo_url && hero && (
          <img
            src={site.logo_url}
            alt=""
            className="absolute bottom-2 left-2 h-9 rounded-full bg-white/90 backdrop-blur p-1 shadow"
          />
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-serif-display text-lg leading-tight truncate">{site.farm_name}</div>
            {site.tagline && (
              <div className="mt-0.5 text-xs text-[#3A3830] line-clamp-1">{site.tagline}</div>
            )}
          </div>
          <span
            className="shrink-0 h-3 w-3 rounded-full border border-black/10 mt-1"
            style={{ background: accent }}
            title={accent}
          />
        </div>

        <div className="mt-2 flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
          <Globe size={10} weight="fill" />
          <Link
            to={`/f/${site.slug}`}
            target="_blank"
            rel="noreferrer"
            className="truncate hover:text-[#1B4332]"
          >
            /f/{site.slug}
          </Link>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Link
            to={`/farmers/websites/${site.id}`}
            data-testid={`farmers-site-card-edit-${site.slug}`}
            className="inline-flex items-center gap-1.5 rounded-full text-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            style={{ background: accent }}
          >
            <PencilSimple size={11} weight="bold" /> Edit
          </Link>
          {isPublished ? (
            <Link
              to={`/f/${site.slug}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`farmers-site-card-view-${site.slug}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
            >
              View live <ArrowSquareOut size={10} weight="bold" />
            </Link>
          ) : (
            <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
              Publish from the editor
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function PromoCard({ signedIn, loading }) {
  return (
    <div
      data-testid="farmers-website-promo"
      className="rounded-2xl border border-black/10 bg-white shadow-sm p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6"
    >
      <div
        className="hidden md:flex h-24 w-24 rounded-2xl items-center justify-center shrink-0"
        style={{ background: "linear-gradient(135deg,#B85F2A 0%, #241209 100%)" }}
      >
        <Storefront size={36} weight="fill" className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          <Sparkle size={12} weight="fill" className="inline mr-1" />
          Farm Website Builder
        </div>
        <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">
          Give your farm a beautiful website in minutes.
        </h2>
        <p className="mt-1.5 text-sm text-[#3A3830] max-w-2xl">
          Pick a theme, drop in your logo, list your produce and farm stays. Or paste any existing farm-site URL and our AI clones the whole thing into an editable copy.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/farmers/websites/new"
            data-testid="farmers-promo-new"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> Start from scratch
          </Link>
          <Link
            to="/farmers/websites/import"
            data-testid="farmers-promo-clone"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >
            <LinkSimple size={11} weight="bold" /> Clone from URL
          </Link>
          {!signedIn && !loading && (
            <span className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
              Sign in to save your site <ArrowRight size={10} weight="bold" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
